import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MoreHorizontal,
  Plus,
  Search,
  FlaskConical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Badge } from '@/components/ui/badge';
import { UserAccountMenu } from '@/components/layout/UserAccountMenu';
import { EditFieldTemplateDialog } from '@/components/ai/EditFieldTemplateDialog';
import {
  ProjectHubSettingsDialog,
  type AiTemplateBackupReport,
  type AiTemplateBackupReportEntry,
} from '@/components/projectHub/ProjectHubSettingsDialog';
import { toast } from 'sonner';
import {
  type AppSettings,
  loadAppSettings,
  saveAppSettings,
} from '@/lib/settings';
import {
  buildSettingsRoute,
  getCurrentHashRoute,
  getSettingsReturnRoute,
} from '@/lib/hash-route';
import {
  projectsApi,
  projectTriageTemplatesApi,
  pubmedTextQueryTemplatesApi,
  userTriageTemplatesApi,
  validateFieldDecisionCards,
} from '@/services/api';
import type {
  CreateTriageFieldTemplateRequest,
  PubmedHeaderKey,
  Project,
  ProjectType,
  PubmedTextQueryTemplate,
  TriageTemplateScope,
  UserTriageTemplateDetail,
  UserTriageTemplateListItem,
} from '@pubmed-seq-reader/shared/schemas';
import { authClient } from '@/lib/auth';

interface ProjectHubState {
  projects: Project[];
  loading: boolean;
  showDialog: boolean;
  showSettingsDialog: boolean;
  showDeleteDialog: boolean;
  projectName: string;
  projectDescription: string;
  projectType: ProjectType;
  searchQuery: string;
  pendingDeleteProjectId: string | null;
  pendingDeleteProjectName: string;
}

interface QueryTemplateEditorState {
  templates: PubmedTextQueryTemplate[];
  loading: boolean;
  saving: boolean;
  deletingId: string | null;
  editingId: string | null;
  editingName: string;
  editingQuery: string;
  newName: string;
  newQuery: string;
}

type QueryTemplateEditorAction = {
  type: 'patch';
  payload: Partial<QueryTemplateEditorState>;
};

const INITIAL_QUERY_TEMPLATE_EDITOR_STATE: QueryTemplateEditorState = {
  templates: [],
  loading: false,
  saving: false,
  deletingId: null,
  editingId: null,
  editingName: '',
  editingQuery: '',
  newName: '',
  newQuery: '',
};

const queryTemplateEditorReducer = (
  state: QueryTemplateEditorState,
  action: QueryTemplateEditorAction
): QueryTemplateEditorState => {
  if (action.type === 'patch') {
    return {
      ...state,
      ...action.payload,
    };
  }
  return state;
};

const AI_TEMPLATE_LIST_LIMIT = 100;
const AI_TEMPLATE_DETAIL_FETCH_CONCURRENCY = 4;
const AI_BACKUP_SCHEMA_VERSION = 2;
const AI_BACKUP_SUPPORTED_SCHEMA_VERSIONS = new Set([1, AI_BACKUP_SCHEMA_VERSION]);
const AI_BACKUP_SOURCE = 'pubmed-seq-reader';
const AI_BACKUP_REPORT_LIMIT = 60;
const AI_BACKUP_IMPORT_TARGET_AUTO = '__auto__';

type BackupJsonTemplate = {
  id?: string;
  projectId?: string;
  projectName?: string;
  projectType?: ProjectType;
  name?: string | null;
  scope?: TriageTemplateScope;
  criteriaText?: string;
  reasoningText?: string;
  fields?: unknown;
  pubmedHeaderKeys?: unknown;
  headerCardOrder?: unknown;
  extractorTargets?: unknown;
  provider?: string;
  model?: string;
};

type BackupJsonPayload = {
  schemaVersion?: unknown;
  exportedAt?: unknown;
  source?: unknown;
  templates?: unknown;
};

const normalizeTemplateText = (value: string | null | undefined) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const normalizeProjectNameKey = (value: string | null | undefined) => normalizeTemplateText(value);

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`;
};

const normalizeTemplateScope = (value: unknown): TriageTemplateScope =>
  value === 'builder' || value === 'results_header_builder' || value === 'extractor'
    ? value
    : 'builder';

const normalizeHeaderKeys = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const normalizeHeaderCardOrder = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const normalizeExtractorTargets = (value: unknown): Record<string, 'yes' | 'no'> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([key, decision]) =>
      key.trim().length > 0 && (decision === 'yes' || decision === 'no')
  );
  return Object.fromEntries(entries) as Record<string, 'yes' | 'no'>;
};

const buildTemplateDuplicateKey = (input: {
  projectId: string;
  scope: TriageTemplateScope;
  name?: string | null;
  criteriaText: string;
  reasoningText: string;
  provider: string;
  model: string;
  fields: unknown;
  pubmedHeaderKeys?: unknown;
  headerCardOrder?: unknown;
  extractorTargets?: unknown;
}): string => {
  const normalizedName = normalizeTemplateText(input.name);
  if (normalizedName) {
    return ['name', input.projectId, input.scope, normalizedName].join('|');
  }

  return [
    'content',
    input.projectId,
    input.scope,
    normalizeTemplateText(input.criteriaText),
    normalizeTemplateText(input.reasoningText),
    normalizeTemplateText(input.provider),
    normalizeTemplateText(input.model),
    stableStringify(input.fields),
    stableStringify(input.pubmedHeaderKeys ?? []),
    stableStringify(input.headerCardOrder ?? []),
    stableStringify(input.extractorTargets ?? {}),
  ].join('|');
};

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsText(file);
  });

const mapWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let cursor = 0;
  const workerCount = Math.min(Math.max(1, concurrency), items.length);

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (cursor < items.length) {
        const currentIndex = cursor;
        cursor += 1;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    })
  );

  return results;
};

const toReportName = (input: { name?: string | null; fields?: unknown; id?: string }) => {
  const named = typeof input.name === 'string' ? input.name.trim() : '';
  if (named) return named;
  if (Array.isArray(input.fields) && input.fields.length > 0) {
    const first = input.fields[0] as { label?: unknown };
    if (typeof first?.label === 'string' && first.label.trim()) {
      return first.label.trim();
    }
  }
  return input.id || '(unnamed)';
};

const normalizeBackupTemplate = (template: BackupJsonTemplate): {
  templateId: string | null;
  templateName: string;
  projectId: string;
  projectName: string | null;
  projectType: ProjectType | null;
  payload: CreateTriageFieldTemplateRequest;
} => {
  const templateId = typeof template.id === 'string' ? template.id : null;
  const templateName = toReportName({
    name: template.name,
    fields: template.fields,
    id: templateId || undefined,
  });

  const projectId = typeof template.projectId === 'string' ? template.projectId.trim() : '';
  if (!projectId) {
    throw new Error('Missing projectId');
  }
  const projectName = typeof template.projectName === 'string' ? template.projectName.trim() : '';
  const projectType = template.projectType === 'pdf' || template.projectType === 'pubmed' ? template.projectType : null;

  const criteriaText = typeof template.criteriaText === 'string' ? template.criteriaText.trim() : '';
  if (!criteriaText) {
    throw new Error('Missing criteriaText');
  }

  const reasoningText = typeof template.reasoningText === 'string' ? template.reasoningText.trim() : '';
  if (!reasoningText) {
    throw new Error('Missing reasoningText');
  }

  const provider = typeof template.provider === 'string' ? template.provider.trim() : '';
  if (!provider) {
    throw new Error('Missing provider');
  }

  const model = typeof template.model === 'string' ? template.model.trim() : '';
  if (!model) {
    throw new Error('Missing model');
  }

  const fields = validateFieldDecisionCards(template.fields);
  const name = typeof template.name === 'string' ? template.name.trim() : '';
  const scope = normalizeTemplateScope(template.scope);
  const pubmedHeaderKeys = normalizeHeaderKeys(template.pubmedHeaderKeys) as PubmedHeaderKey[];
  const headerCardOrder = normalizeHeaderCardOrder(template.headerCardOrder);
  const extractorTargets = normalizeExtractorTargets(template.extractorTargets);

  const payload: CreateTriageFieldTemplateRequest = {
    ...(name ? { name } : {}),
    scope,
    criteriaText,
    reasoningText,
    fields,
    provider,
    model,
    ...(pubmedHeaderKeys.length > 0 ? { pubmedHeaderKeys } : {}),
    ...(headerCardOrder.length > 0 ? { headerCardOrder } : {}),
    ...(Object.keys(extractorTargets).length > 0 ? { extractorTargets } : {}),
  };

  return {
    templateId,
    templateName,
    projectId,
    projectName: projectName || null,
    projectType,
    payload,
  };
};

export default function ProjectHub() {
  const { t } = useTranslation();
  const sessionState = authClient.useSession();
  const session = (
    sessionState.data || null
  ) as { user?: { role?: string; name?: string | null; email?: string | null } } | null;
  const [settings, setSettings] = useState<AppSettings>(() => loadAppSettings());
  const [settingsPanel, setSettingsPanel] = useState<
    'profile' | 'pubmedTemplates' | 'aiTemplates' | 'aiTemplateBackup' | 'ocr' | 'ncbi' | 'templates'
  >('profile');
  const [profileName, setProfileName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [queryTemplateState, dispatchQueryTemplateState] = useReducer(
    queryTemplateEditorReducer,
    INITIAL_QUERY_TEMPLATE_EDITOR_STATE
  );
  const [state, setState] = useState<ProjectHubState>({
    projects: [],
    loading: false,
    showDialog: false,
    showSettingsDialog: false,
    showDeleteDialog: false,
    projectName: '',
    projectDescription: '',
    projectType: 'pubmed',
    searchQuery: '',
    pendingDeleteProjectId: null,
    pendingDeleteProjectName: '',
  });

  const queryTemplates = queryTemplateState.templates;
  const queryTemplatesLoading = queryTemplateState.loading;
  const queryTemplatesSaving = queryTemplateState.saving;
  const queryTemplatesDeletingId = queryTemplateState.deletingId;
  const editingQueryTemplateId = queryTemplateState.editingId;
  const editingQueryTemplateName = queryTemplateState.editingName;
  const editingQueryTemplateQuery = queryTemplateState.editingQuery;
  const newTemplateName = queryTemplateState.newName;
  const newTemplateQuery = queryTemplateState.newQuery;
  const [aiTemplates, setAiTemplates] = useState<UserTriageTemplateListItem[]>([]);
  const [aiTemplatesLoading, setAiTemplatesLoading] = useState(false);
  const [aiTemplateScopeFilter, setAiTemplateScopeFilter] = useState<'all' | TriageTemplateScope>('all');
  const [aiTemplateProjectFilter, setAiTemplateProjectFilter] = useState('all');
  const [aiTemplateKeyword, setAiTemplateKeyword] = useState('');
  const [aiTemplateDeleteDialogOpen, setAiTemplateDeleteDialogOpen] = useState(false);
  const [aiTemplatePendingDelete, setAiTemplatePendingDelete] = useState<UserTriageTemplateListItem | null>(null);
  const [aiTemplateDeletingId, setAiTemplateDeletingId] = useState<string | null>(null);
  const [aiTemplateEditDialogOpen, setAiTemplateEditDialogOpen] = useState(false);
  const [aiTemplateEditLoading, setAiTemplateEditLoading] = useState(false);
  const [aiTemplateEditSaving, setAiTemplateEditSaving] = useState(false);
  const [aiTemplateEditingId, setAiTemplateEditingId] = useState<string | null>(null);
  const [aiTemplateEditingName, setAiTemplateEditingName] = useState('');
  const [aiTemplateEditingFieldsJson, setAiTemplateEditingFieldsJson] = useState('');
  const [aiBackupExporting, setAiBackupExporting] = useState(false);
  const [aiBackupExportProgress, setAiBackupExportProgress] = useState('');
  const [aiBackupImporting, setAiBackupImporting] = useState(false);
  const [aiBackupImportFile, setAiBackupImportFile] = useState<File | null>(null);
  const [aiBackupImportFileName, setAiBackupImportFileName] = useState('');
  const [aiBackupImportTargetProjectId, setAiBackupImportTargetProjectId] = useState<string>(
    AI_BACKUP_IMPORT_TARGET_AUTO
  );
  const [aiBackupLastReport, setAiBackupLastReport] = useState<AiTemplateBackupReport | null>(null);

  const setQueryTemplates = (templates: PubmedTextQueryTemplate[]) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { templates } });
  };

  const setQueryTemplatesLoading = (loading: boolean) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { loading } });
  };

  const setQueryTemplatesSaving = (saving: boolean) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { saving } });
  };

  const setQueryTemplatesDeletingId = (deletingId: string | null) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { deletingId } });
  };

  const setEditingQueryTemplateId = (editingId: string | null) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { editingId } });
  };

  const setEditingQueryTemplateName = (editingName: string) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { editingName } });
  };

  const setEditingQueryTemplateQuery = (editingQuery: string) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { editingQuery } });
  };

  const setNewTemplateName = (newName: string) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { newName } });
  };

  const setNewTemplateQuery = (newQuery: string) => {
    dispatchQueryTemplateState({ type: 'patch', payload: { newQuery } });
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    const nextName = session?.user?.name?.trim() || '';
    setProfileName(prev => (prev === nextName ? prev : nextName));
  }, [session?.user?.name]);

  useEffect(() => {
    saveAppSettings(settings);
  }, [settings]);

  useEffect(() => {
    if (aiBackupImportTargetProjectId === AI_BACKUP_IMPORT_TARGET_AUTO) return;
    if (state.projects.some(project => project.id === aiBackupImportTargetProjectId)) return;
    setAiBackupImportTargetProjectId(AI_BACKUP_IMPORT_TARGET_AUTO);
  }, [aiBackupImportTargetProjectId, state.projects]);

  const loadProjects = async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const projects = await projectsApi.list();
      setState(prev => ({
        ...prev,
        projects,
        loading: false,
      }));
    } catch (err) {
      toast.error('Failed to load projects', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
      setState(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleOpenDialog = () => {
    setState(prev => ({
      ...prev,
      showDialog: true,
      projectName: '',
      projectDescription: '',
      projectType: 'pubmed',
    }));
  };

  const handleCloseSettingsDialog = () => {
    const currentRoute = getCurrentHashRoute();
    if (currentRoute.startsWith('/settings')) {
      window.location.hash = getSettingsReturnRoute(currentRoute);
      return;
    }
    setState(prev => ({ ...prev, showSettingsDialog: false }));
  };

  const handleSettingsDialogOpenChange = (open: boolean) => {
    if (open) {
      const currentRoute = getCurrentHashRoute();
      if (!currentRoute.startsWith('/settings')) {
        window.location.hash = buildSettingsRoute(currentRoute);
      }
      return;
    }
    handleCloseSettingsDialog();
  };

  const loadQueryTemplates = async () => {
    setQueryTemplatesLoading(true);
    try {
      const templates = await pubmedTextQueryTemplatesApi.list(500);
      setQueryTemplates(templates);
    } catch (error) {
      toast.error('Failed to load query templates', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setQueryTemplatesLoading(false);
    }
  };

  const loadAiTemplates = async () => {
    setAiTemplatesLoading(true);
    try {
      const response = await userTriageTemplatesApi.list({
        ...(aiTemplateProjectFilter !== 'all' ? { projectId: aiTemplateProjectFilter } : {}),
        ...(aiTemplateScopeFilter !== 'all' ? { scope: aiTemplateScopeFilter } : {}),
        ...(aiTemplateKeyword.trim() ? { keyword: aiTemplateKeyword.trim() } : {}),
        limit: AI_TEMPLATE_LIST_LIMIT,
        offset: 0,
      });
      setAiTemplates(response.items);
    } catch (error) {
      toast.error('Failed to load AI templates', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiTemplatesLoading(false);
    }
  };

  const listAllAiTemplateSummaries = async () => {
    const allItems: UserTriageTemplateListItem[] = [];
    let offset = 0;

    while (true) {
      if (offset > 5000) {
        throw new Error('Template list exceeds the maximum supported offset (5000)');
      }
      const response = await userTriageTemplatesApi.list({
        limit: AI_TEMPLATE_LIST_LIMIT,
        offset,
      });
      allItems.push(...response.items);
      if (response.items.length < AI_TEMPLATE_LIST_LIMIT) {
        break;
      }
      offset += response.items.length;
    }

    return allItems;
  };

  const fetchTemplateDetails = async (
    items: UserTriageTemplateListItem[],
    onProgress?: (finished: number, total: number) => void
  ): Promise<UserTriageTemplateDetail[]> => {
    let finished = 0;
    return mapWithConcurrency(items, AI_TEMPLATE_DETAIL_FETCH_CONCURRENCY, async item => {
      const detail = await userTriageTemplatesApi.getById(item.id);
      finished += 1;
      if (onProgress) {
        onProgress(finished, items.length);
      }
      return detail;
    });
  };

  const buildExistingDuplicateKeys = (details: UserTriageTemplateDetail[]) => {
    const keys = new Set<string>();
    for (const detail of details) {
      keys.add(
        buildTemplateDuplicateKey({
          projectId: detail.projectId,
          scope: detail.scope,
          name: detail.name || null,
          criteriaText: detail.criteriaText,
          reasoningText: detail.reasoningText,
          provider: detail.provider,
          model: detail.model,
          fields: detail.fields,
          pubmedHeaderKeys: detail.pubmedHeaderKeys,
          headerCardOrder: detail.headerCardOrder,
          extractorTargets: detail.extractorTargets,
        })
      );
    }
    return keys;
  };

  const handleSelectAiBackupImportFile = (file: File | null) => {
    setAiBackupImportFile(file);
    setAiBackupImportFileName(file?.name || '');
  };

  const handleExportAiTemplateBackup = async () => {
    if (aiBackupExporting || aiBackupImporting) return;

    setAiBackupExporting(true);
    setAiBackupExportProgress('Listing templates...');
    try {
      const allItems = await listAllAiTemplateSummaries();
      setAiBackupExportProgress(`Fetching details 0/${allItems.length}...`);

      const details = await fetchTemplateDetails(allItems, (finished, total) => {
        if (finished === total || finished % 10 === 0) {
          setAiBackupExportProgress(`Fetching details ${finished}/${total}...`);
        }
      });
      const summaryById = new Map(allItems.map(item => [item.id, item]));
      const templatesForBackup = details.map(detail => {
        const summary = summaryById.get(detail.id);
        return {
          ...detail,
          ...(summary?.projectName ? { projectName: summary.projectName } : {}),
          ...(summary?.projectType ? { projectType: summary.projectType } : {}),
        };
      });

      const payload = {
        schemaVersion: AI_BACKUP_SCHEMA_VERSION,
        exportedAt: new Date().toISOString(),
        source: AI_BACKUP_SOURCE,
        templates: templatesForBackup,
      };

      const fileName = `ai-template-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);

      toast.success('AI template backup exported', {
        description: `Exported ${details.length} template(s).`,
      });
    } catch (error) {
      toast.error('Failed to export AI template backup', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiBackupExporting(false);
      setAiBackupExportProgress('');
    }
  };

  const handleImportAiTemplateBackup = async () => {
    if (aiBackupImporting || aiBackupExporting) return;
    if (!aiBackupImportFile) {
      toast.error('Select a backup JSON file first');
      return;
    }

    const startedAt = new Date().toISOString();
    setAiBackupImporting(true);

    try {
      const text = await readFileAsText(aiBackupImportFile);
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Invalid JSON file');
      }

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Invalid backup format');
      }

      const backup = parsed as BackupJsonPayload;
      const schemaVersion = Number(backup.schemaVersion);
      if (!Number.isInteger(schemaVersion) || !AI_BACKUP_SUPPORTED_SCHEMA_VERSIONS.has(schemaVersion)) {
        throw new Error(`Unsupported schemaVersion: ${String(backup.schemaVersion)}`);
      }
      if (!Array.isArray(backup.templates)) {
        throw new Error('Invalid backup format: templates must be an array');
      }

      const templates = backup.templates as BackupJsonTemplate[];
      const projectIdSet = new Set(state.projects.map(project => project.id));
      const projectNameMap = new Map<string, Project[]>();
      for (const project of state.projects) {
        const key = normalizeProjectNameKey(project.name);
        if (!key) continue;
        const current = projectNameMap.get(key) || [];
        current.push(project);
        projectNameMap.set(key, current);
      }
      const autoFallbackProjectId =
        state.projects.find(project => project.status === 'active')?.id || state.projects[0]?.id || null;
      const configuredFallbackProjectId =
        aiBackupImportTargetProjectId !== AI_BACKUP_IMPORT_TARGET_AUTO &&
        projectIdSet.has(aiBackupImportTargetProjectId)
          ? aiBackupImportTargetProjectId
          : null;
      const resolveProjectIdByName = (projectName: string | null, projectType: ProjectType | null) => {
        const key = normalizeProjectNameKey(projectName);
        if (!key) return null;
        const candidates = projectNameMap.get(key);
        if (!candidates || candidates.length === 0) return null;
        if (projectType) {
          const matchedByType = candidates.find(project => project.type === projectType);
          if (matchedByType) return matchedByType.id;
        }
        return candidates[0].id;
      };
      const existingItems = await listAllAiTemplateSummaries();
      const existingDetails = await fetchTemplateDetails(existingItems);
      const duplicateKeys = buildExistingDuplicateKeys(existingDetails);

      let imported = 0;
      let skippedDuplicate = 0;
      let skippedMissingProject = 0;
      let failed = 0;
      const reportEntries: AiTemplateBackupReportEntry[] = [];

      for (const rawTemplate of templates) {
        const fallbackTemplateId = typeof rawTemplate?.id === 'string' ? rawTemplate.id : null;
        const fallbackTemplateName = toReportName({
          name: typeof rawTemplate?.name === 'string' ? rawTemplate.name : null,
          fields: rawTemplate?.fields,
          id: fallbackTemplateId || undefined,
        });
        const fallbackProjectId =
          typeof rawTemplate?.projectId === 'string' ? rawTemplate.projectId : '';

        try {
          const normalized = normalizeBackupTemplate(rawTemplate);
          let targetProjectId = normalized.projectId;

          if (!projectIdSet.has(targetProjectId)) {
            targetProjectId =
              resolveProjectIdByName(normalized.projectName, normalized.projectType) ||
              configuredFallbackProjectId ||
              autoFallbackProjectId ||
              '';
          }

          if (!projectIdSet.has(targetProjectId)) {
            skippedMissingProject += 1;
            if (reportEntries.length < AI_BACKUP_REPORT_LIMIT) {
              reportEntries.push({
                status: 'skippedMissingProject',
                templateId: normalized.templateId,
                templateName: normalized.templateName,
                projectId: normalized.projectId,
                reason:
                  'Project does not exist in current account, and no available target project was found',
              });
            }
            continue;
          }

          const duplicateKey = buildTemplateDuplicateKey({
            projectId: targetProjectId,
            scope: normalized.payload.scope ?? 'builder',
            name: normalized.payload.name || null,
            criteriaText: normalized.payload.criteriaText,
            reasoningText: normalized.payload.reasoningText,
            provider: normalized.payload.provider,
            model: normalized.payload.model,
            fields: normalized.payload.fields,
            pubmedHeaderKeys: normalized.payload.pubmedHeaderKeys ?? [],
            headerCardOrder: normalized.payload.headerCardOrder ?? [],
            extractorTargets: normalized.payload.extractorTargets ?? {},
          });

          if (duplicateKeys.has(duplicateKey)) {
            skippedDuplicate += 1;
            if (reportEntries.length < AI_BACKUP_REPORT_LIMIT) {
              reportEntries.push({
                status: 'skippedDuplicate',
                templateId: normalized.templateId,
                templateName: normalized.templateName,
                projectId: targetProjectId,
                reason: 'Duplicate template key',
              });
            }
            continue;
          }

          await projectTriageTemplatesApi.create(targetProjectId, normalized.payload);
          duplicateKeys.add(duplicateKey);
          imported += 1;
          if (reportEntries.length < AI_BACKUP_REPORT_LIMIT) {
            reportEntries.push({
              status: 'imported',
              templateId: normalized.templateId,
              templateName: normalized.templateName,
              projectId: targetProjectId,
            });
          }
        } catch (error) {
          failed += 1;
          if (reportEntries.length < AI_BACKUP_REPORT_LIMIT) {
            reportEntries.push({
              status: 'failed',
              templateId: fallbackTemplateId,
              templateName: fallbackTemplateName,
              projectId: fallbackProjectId,
              reason: error instanceof Error ? error.message : 'Import failed',
            });
          }
        }
      }

      const report: AiTemplateBackupReport = {
        fileName: aiBackupImportFile.name,
        startedAt,
        finishedAt: new Date().toISOString(),
        total: templates.length,
        imported,
        skippedDuplicate,
        skippedMissingProject,
        failed,
        entries: reportEntries,
      };
      setAiBackupLastReport(report);
      await loadAiTemplates();

      const summaryDescription = `Imported ${imported}, skipped duplicate ${skippedDuplicate}, skipped missing project ${skippedMissingProject}, failed ${failed}.`;
      if (imported > 0) {
        toast.success('AI template backup import completed', {
          description: summaryDescription,
        });
      } else if (failed > 0 || skippedMissingProject > 0) {
        toast.warning('AI template backup import finished with warnings', {
          description: summaryDescription,
        });
      } else {
        toast.success('AI template backup import completed', {
          description: summaryDescription,
        });
      }
    } catch (error) {
      toast.error('Failed to import AI template backup', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiBackupImporting(false);
    }
  };

  useEffect(() => {
    if (!state.showSettingsDialog) return;
    if (settingsPanel !== 'pubmedTemplates' && settingsPanel !== 'templates') return;
    void loadQueryTemplates();
  }, [settingsPanel, state.showSettingsDialog]);

  useEffect(() => {
    if (!state.showSettingsDialog) return;
    if (settingsPanel !== 'aiTemplates') return;

    const timer = window.setTimeout(() => {
      void loadAiTemplates();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [settingsPanel, state.showSettingsDialog, aiTemplateKeyword, aiTemplateProjectFilter, aiTemplateScopeFilter]);

  useEffect(() => {
    const syncSettingsDialogByRoute = () => {
      const open = getCurrentHashRoute().startsWith('/settings');
      setState(prev => (prev.showSettingsDialog === open ? prev : { ...prev, showSettingsDialog: open }));
      if (open) {
        setSettingsPanel('profile');
      }
    };
    syncSettingsDialogByRoute();
    window.addEventListener('hashchange', syncSettingsDialogByRoute);
    return () => window.removeEventListener('hashchange', syncSettingsDialogByRoute);
  }, []);

  const handleSaveProfile = async () => {
    const name = profileName.trim();
    if (!name) {
      toast.error('Nickname is required');
      return;
    }

    setProfileSaving(true);
    try {
      const result = await (authClient as any).updateUser({
        name,
      });
      if (result?.error) {
        toast.error(result.error.message || 'Failed to update profile');
        return;
      }
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('Current password and new password are required');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setPasswordChanging(true);
    try {
      const result = await (authClient as any).changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      });
      if (result?.error) {
        toast.error(result.error.message || 'Failed to change password');
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Password updated');
    } catch (error) {
      toast.error('Failed to change password', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleStartEditQueryTemplate = (template: PubmedTextQueryTemplate) => {
    setEditingQueryTemplateId(template.id);
    setEditingQueryTemplateName(template.name);
    setEditingQueryTemplateQuery(template.query);
  };

  const handleCancelEditQueryTemplate = () => {
    setEditingQueryTemplateId(null);
    setEditingQueryTemplateName('');
    setEditingQueryTemplateQuery('');
  };

  const handleSaveEditQueryTemplate = async () => {
    if (!editingQueryTemplateId) return;
    const name = editingQueryTemplateName.trim();
    const query = editingQueryTemplateQuery.trim();
    if (!name || !query) {
      toast.error('Template name and query are required');
      return;
    }

    setQueryTemplatesSaving(true);
    try {
      await pubmedTextQueryTemplatesApi.update(editingQueryTemplateId, { name, query });
      await loadQueryTemplates();
      handleCancelEditQueryTemplate();
      toast.success('Template updated');
    } catch (error) {
      toast.error('Failed to update template', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setQueryTemplatesSaving(false);
    }
  };

  const handleDeleteQueryTemplate = async (template: PubmedTextQueryTemplate) => {
    const confirmed = window.confirm(`Delete template "${template.name}"?`);
    if (!confirmed) return;

    setQueryTemplatesDeletingId(template.id);
    try {
      await pubmedTextQueryTemplatesApi.delete(template.id);
      await loadQueryTemplates();
      if (editingQueryTemplateId === template.id) {
        handleCancelEditQueryTemplate();
      }
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setQueryTemplatesDeletingId(null);
    }
  };

  const handleCreateQueryTemplate = async () => {
    const name = newTemplateName.trim();
    const query = newTemplateQuery.trim();
    if (!name || !query) {
      toast.error('Template name and query are required');
      return;
    }

    setQueryTemplatesSaving(true);
    try {
      await pubmedTextQueryTemplatesApi.create({ name, query });
      setNewTemplateName('');
      setNewTemplateQuery('');
      await loadQueryTemplates();
      toast.success('Template created');
    } catch (error) {
      toast.error('Failed to create template', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setQueryTemplatesSaving(false);
    }
  };

  const formatTemplateScopeLabel = (scope: TriageTemplateScope) => {
    if (scope === 'results_header_builder') return t('ai.dialogs.import.context.tab3-results-header');
    if (scope === 'extractor') return t('ai.dialogs.import.context.tab2-extractor');
    return t('ai.dialogs.import.context.tab1-builder');
  };

  const handleOpenTemplateProject = (template: UserTriageTemplateListItem) => {
    const params = new URLSearchParams({ projectId: template.projectId });
    window.location.hash = template.projectType === 'pdf' ? `/pdf-ai?${params.toString()}` : `/ai?${params.toString()}`;
  };

  const handleOpenEditAiTemplate = async (template: UserTriageTemplateListItem) => {
    setAiTemplateEditingId(template.id);
    setAiTemplateEditingName(template.name?.trim() || template.firstFieldLabel || template.id);
    setAiTemplateEditingFieldsJson('[]');
    setAiTemplateEditLoading(true);
    setAiTemplateEditDialogOpen(true);

    try {
      const detail = await userTriageTemplatesApi.getById(template.id);
      setAiTemplateEditingName(detail.name?.trim() || detail.fields[0]?.label || detail.id);
      setAiTemplateEditingFieldsJson(JSON.stringify(detail.fields, null, 2));
    } catch (error) {
      setAiTemplateEditDialogOpen(false);
      toast.error('Failed to load template detail', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiTemplateEditLoading(false);
    }
  };

  const handleConfirmEditAiTemplate = async () => {
    if (!aiTemplateEditingId) return;

    const name = aiTemplateEditingName.trim();
    if (!name) {
      toast.error('Template name is required');
      return;
    }

    let fields = [] as ReturnType<typeof validateFieldDecisionCards>;
    try {
      fields = validateFieldDecisionCards(JSON.parse(aiTemplateEditingFieldsJson) as unknown);
    } catch (error) {
      const description = error instanceof Error ? error.message : 'Invalid fields JSON';
      toast.error('Invalid fields JSON', { description });
      return;
    }

    setAiTemplateEditSaving(true);
    try {
      const updated = await userTriageTemplatesApi.update(aiTemplateEditingId, {
        name,
        fields,
      });

      setAiTemplates(prev =>
        prev.map(item =>
          item.id === updated.id
            ? {
                ...item,
                name: updated.name ?? null,
                scope: updated.scope,
                provider: updated.provider,
                model: updated.model,
                updatedAt: updated.updatedAt,
                firstFieldKey: updated.fields[0]?.key || null,
                firstFieldLabel: updated.fields[0]?.label || null,
              }
            : item
        )
      );

      setAiTemplateEditDialogOpen(false);
      toast.success('Template updated');
    } catch (error) {
      toast.error('Failed to update template', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiTemplateEditSaving(false);
    }
  };

  const handleRequestDeleteAiTemplate = (template: UserTriageTemplateListItem) => {
    setAiTemplatePendingDelete(template);
    setAiTemplateDeleteDialogOpen(true);
  };

  const handleCloseAiTemplateDeleteDialog = (open = false) => {
    if (open) {
      setAiTemplateDeleteDialogOpen(true);
      return;
    }
    if (aiTemplateDeletingId) return;
    setAiTemplateDeleteDialogOpen(false);
    setAiTemplatePendingDelete(null);
  };

  const handleConfirmDeleteAiTemplate = async () => {
    if (!aiTemplatePendingDelete) return;
    setAiTemplateDeletingId(aiTemplatePendingDelete.id);
    try {
      await userTriageTemplatesApi.delete(aiTemplatePendingDelete.id);
      setAiTemplates(prev => prev.filter(item => item.id !== aiTemplatePendingDelete.id));
      setAiTemplateDeleteDialogOpen(false);
      setAiTemplatePendingDelete(null);
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setAiTemplateDeletingId(null);
    }
  };

  const handleAiTemplateEditDialogOpenChange = (open: boolean) => {
    if (aiTemplateEditSaving) return;
    setAiTemplateEditDialogOpen(open);
    if (!open) {
      setAiTemplateEditingId(null);
      setAiTemplateEditingName('');
      setAiTemplateEditingFieldsJson('');
      setAiTemplateEditLoading(false);
    }
  };

  const handleAddOcrApiKey = () => {
    setSettings(prev => ({
      ...prev,
      ocrApiKeys: [...prev.ocrApiKeys, ''],
    }));
  };

  const handleChangeOcrApiKey = (index: number, value: string) => {
    setSettings(prev => {
      const next = [...prev.ocrApiKeys];
      next[index] = value;
      return {
        ...prev,
        ocrApiKeys: next,
      };
    });
  };

  const handleRemoveOcrApiKey = (index: number) => {
    setSettings(prev => ({
      ...prev,
      ocrApiKeys: prev.ocrApiKeys.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleOpenPubmedScreen = (projectId: string) => {
    const params = new URLSearchParams({ projectId });
    window.location.hash = `/pubmed?${params.toString()}`;
  };

  const handleOpenAiSecondRound = (projectId: string) => {
    const params = new URLSearchParams({ projectId });
    window.location.hash = `/ai?${params.toString()}`;
  };

  const handleOpenPdfAiSecondRound = (projectId: string) => {
    const params = new URLSearchParams({ projectId });
    window.location.hash = `/pdf-ai?${params.toString()}`;
  };

  const handleOpenPdfOcrScreen = (projectId: string) => {
    const params = new URLSearchParams({ projectId });
    window.location.hash = `/pdf-ocr?${params.toString()}`;
  };

  const handleCloseDialog = () => {
    setState(prev => ({
      ...prev,
      showDialog: false,
      projectName: '',
      projectDescription: '',
      projectType: 'pubmed',
    }));
  };

  const handleCreateProject = async () => {
    if (!state.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true }));

      const project = await projectsApi.create({
        name: state.projectName.trim(),
        description: state.projectDescription.trim() || null,
        type: state.projectType,
      });

      setState(prev => ({
        ...prev,
        projects: [project, ...prev.projects],
        showDialog: false,
        projectName: '',
        projectDescription: '',
        projectType: 'pubmed',
        loading: false,
      }));
    } catch (err) {
      toast.error('Failed to create project', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
      setState(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleOpenDeleteDialog = (projectId: string, projectName: string) => {
    setState(prev => ({
      ...prev,
      showDeleteDialog: true,
      pendingDeleteProjectId: projectId,
      pendingDeleteProjectName: projectName,
    }));
  };

  const handleCloseDeleteDialog = () => {
    setState(prev => ({
      ...prev,
      showDeleteDialog: false,
      pendingDeleteProjectId: null,
      pendingDeleteProjectName: '',
    }));
  };

  const handleDeleteProject = async () => {
    const projectId = state.pendingDeleteProjectId;
    if (!projectId) return;

    try {
      await projectsApi.delete(projectId);

      setState(prev => ({
        ...prev,
        projects: prev.projects.filter(p => p.id !== projectId),
        showDeleteDialog: false,
        pendingDeleteProjectId: null,
        pendingDeleteProjectName: '',
      }));
      toast.success(t('projectHub.toast.projectDeleted'));
    } catch (err) {
      toast.error(t('projectHub.toast.failedDeleteProject'), {
        description: err instanceof Error ? err.message : t('common.messages.tryAgain'),
      });
    }
  };

  const filteredProjects = useMemo(() => {
    const query = state.searchQuery.trim().toLowerCase();
    const projects = state.projects
      .filter(project => {
        if (!query) return true;
        return (
          project.name.toLowerCase().includes(query) ||
          project.id.toLowerCase().includes(query) ||
          (project.description || '').toLowerCase().includes(query)
        );
      });

    return projects.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [state.projects, state.searchQuery]);

  const aiTemplateLimitReached = aiTemplates.length === AI_TEMPLATE_LIST_LIMIT;

  return (
    <div className="min-h-screen bg-[#f6f7f8] px-4 py-6 md:px-8 md:py-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-[1400px] flex-col">
        <header className="mb-8 flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 md:text-3xl">
            {t('projectHub.title')}
          </h1>
          <UserAccountMenu />
        </header>

        {/* Main Content */}
        <div className="flex flex-1 flex-col gap-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-2.5">
              <div className="relative w-full max-w-[360px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="search"
                  name="project-filter"
                  autoComplete="off"
                  placeholder={t('projectHub.filterPlaceholder')}
                  className="h-10 rounded-xl border-slate-300 pl-9 text-sm"
                  value={state.searchQuery}
                  onChange={e => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
              </div>

            </div>

            <div className="flex items-center gap-2">
                <Button
                onClick={handleOpenDialog}
                className="h-10 rounded-xl px-4 text-sm font-semibold"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t('common.actions.addProject')}
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <Table className="[&_td]:py-3.5 [&_th]:h-12 [&_th]:text-sm">
              <TableHeader className="border-b border-slate-200 bg-white">
                <TableRow className="hover:bg-white">
                  <TableHead>
                    {t('projectHub.table.project')}
                  </TableHead>
                  <TableHead>
                    {t('projectHub.table.type')}
                  </TableHead>
                  <TableHead>
                    {t('projectHub.table.title')}
                  </TableHead>
                  <TableHead>
                    {t('projectHub.table.description')}
                  </TableHead>
                  <TableHead>
                    {t('projectHub.table.createdAt')}
                  </TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                      {t('common.messages.loading')}
                    </TableCell>
                  </TableRow>
                ) : filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                      {state.projects.length === 0
                        ? t('projectHub.empty.noProjects')
                        : t('projectHub.empty.noResults')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map(project => {
                    const createdAtLabel = new Date(project.createdAt).toLocaleString();

                    return (
                      <ContextMenu key={project.id} modal={false}>
                        <ContextMenuTrigger asChild>
                          <TableRow className="hover:bg-slate-50/80">
                            <TableCell className="font-mono text-xs font-medium text-slate-900">
                              {project.id}
                            </TableCell>
                            <TableCell>
                              <Badge variant={project.type === 'pdf' ? 'secondary' : 'outline'}>
                                {project.type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="block max-w-[360px] truncate text-sm font-medium text-slate-900">
                                {project.name}
                              </span>
                            </TableCell>
                            <TableCell>
                              <p className="max-w-[460px] truncate text-sm text-slate-700">
                                {project.description || '-'}
                              </p>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">{createdAtLabel}</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {project.type === 'pdf' ? (
                                    <>
                                      <DropdownMenuItem onClick={() => handleOpenPdfOcrScreen(project.id)}>
                                        {t('projectHub.actions.pdfOcr')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleOpenPdfAiSecondRound(project.id)}>
                                        {t('projectHub.actions.aiSecondRound')}
                                      </DropdownMenuItem>
                                    </>
                                  ) : (
                                    <>
                                      <DropdownMenuItem onClick={() => handleOpenPubmedScreen(project.id)}>
                                        {t('projectHub.actions.pubmedFirstRound')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleOpenAiSecondRound(project.id)}>
                                        {t('projectHub.actions.aiSecondRound')}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleOpenDeleteDialog(project.id, project.name)}
                                    className="text-red-600"
                                  >
                                    {t('projectHub.actions.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        </ContextMenuTrigger>
                        <ContextMenuContent className="w-56">
                          {project.type === 'pdf' ? (
                            <>
                              <ContextMenuItem onClick={() => handleOpenPdfOcrScreen(project.id)}>
                                {t('projectHub.actions.pdfOcr')}
                              </ContextMenuItem>
                              <ContextMenuItem onClick={() => handleOpenPdfAiSecondRound(project.id)}>
                                {t('projectHub.actions.aiSecondRound')}
                              </ContextMenuItem>
                            </>
                          ) : (
                            <>
                              <ContextMenuItem onClick={() => handleOpenPubmedScreen(project.id)}>
                                {t('projectHub.actions.pubmedFirstRound')}
                              </ContextMenuItem>
                              <ContextMenuItem onClick={() => handleOpenAiSecondRound(project.id)}>
                                {t('projectHub.actions.aiSecondRound')}
                              </ContextMenuItem>
                            </>
                          )}
                        </ContextMenuContent>
                      </ContextMenu>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {filteredProjects.length > 0 && (
              <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500">
                {t('projectHub.summary.totalProjects', { count: filteredProjects.length })}
              </div>
            )}
          </div>
        </div>

        <footer className="mt-10 flex flex-col items-center justify-center gap-1 pb-2 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-medium text-slate-600">
            <FlaskConical className="h-4 w-4" />
            {t('projectHub.footer.poweredBy')}
          </p>
          <p className="text-xs text-slate-400">
            {t('projectHub.footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </footer>
      </div>

      {/* Create Project Dialog */}
      <Dialog open={state.showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="border-0 bg-transparent p-0 shadow-none sm:max-w-[860px]">
          <DialogTitle className="sr-only">{t('projectHub.dialogs.create.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectHub.dialogs.create.description')}
          </DialogDescription>
          <Card className="rounded-2xl border-slate-200 bg-white shadow-[0_32px_80px_-30px_rgba(15,23,42,0.5)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-semibold text-slate-900">{t('projectHub.dialogs.create.title')}</CardTitle>
              <CardDescription className="text-sm text-slate-500">
                {t('projectHub.dialogs.create.description')}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">
              {/* Project Type */}
              <div className="grid gap-2">
                <Label htmlFor="project-type" className="text-sm font-medium text-slate-700">
                  {t('projectHub.dialogs.create.projectType')} <span className="text-red-500">*</span>
                </Label>
                <select
                  id="project-type"
                  value={state.projectType}
                  onChange={e => setState(prev => ({ ...prev, projectType: e.target.value as ProjectType }))}
                  className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800"
                >
                  <option value="pubmed">{t('projectHub.dialogs.create.projectTypePubmed')}</option>
                  <option value="pdf">{t('projectHub.dialogs.create.projectTypePdfBeta')}</option>
                </select>
              </div>

              {/* Project Name */}
              <div className="grid gap-2">
                <Label htmlFor="project-name" className="text-sm font-medium text-slate-700">
                  {t('projectHub.dialogs.create.projectName')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="project-name"
                  placeholder={t('projectHub.dialogs.create.projectNamePlaceholder')}
                  value={state.projectName}
                  onChange={e => setState(prev => ({ ...prev, projectName: e.target.value }))}
                  className="h-10 border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>

              {/* Project Description */}
              <div className="grid gap-2">
                <Label htmlFor="project-description" className="text-sm font-medium text-slate-700">
                  {t('projectHub.dialogs.create.descriptionOptional')}
                </Label>
                <Textarea
                  id="project-description"
                  placeholder={t('projectHub.dialogs.create.projectDescriptionPlaceholder')}
                  value={state.projectDescription}
                  onChange={e => setState(prev => ({ ...prev, projectDescription: e.target.value }))}
                  rows={4}
                  className="min-h-[120px] resize-none border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>
            </CardContent>

            <CardFooter className="justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                variant="outline"
                onClick={handleCloseDialog}
                className="border-primary/35 bg-white text-primary hover:bg-primary/10"
              >
                {t('common.dialog.cancel')}
              </Button>
              <Button onClick={handleCreateProject} disabled={state.loading}>
                {state.loading ? t('projectHub.dialogs.create.creating') : t('projectHub.dialogs.create.createProject')}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>

            {/* Settings Dialog */}
      <ProjectHubSettingsDialog
        open={state.showSettingsDialog}
        onOpenChange={handleSettingsDialogOpenChange}
        onClose={handleCloseSettingsDialog}
        sessionEmail={session?.user?.email || ""}
        projects={state.projects}
        settingsPanel={settingsPanel}
        setSettingsPanel={setSettingsPanel}
        settings={settings}
        setSettings={setSettings}
        profileName={profileName}
        setProfileName={setProfileName}
        profileSaving={profileSaving}
        handleSaveProfile={handleSaveProfile}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmNewPassword={confirmNewPassword}
        setConfirmNewPassword={setConfirmNewPassword}
        passwordChanging={passwordChanging}
        handleChangePassword={handleChangePassword}
        aiTemplates={aiTemplates}
        aiTemplateKeyword={aiTemplateKeyword}
        setAiTemplateKeyword={setAiTemplateKeyword}
        aiTemplateProjectFilter={aiTemplateProjectFilter}
        setAiTemplateProjectFilter={setAiTemplateProjectFilter}
        aiTemplateScopeFilter={aiTemplateScopeFilter}
        setAiTemplateScopeFilter={setAiTemplateScopeFilter}
        aiTemplatesLoading={aiTemplatesLoading}
        formatTemplateScopeLabel={formatTemplateScopeLabel}
        handleOpenEditAiTemplate={handleOpenEditAiTemplate}
        handleOpenTemplateProject={handleOpenTemplateProject}
        handleRequestDeleteAiTemplate={handleRequestDeleteAiTemplate}
        aiTemplateDeletingId={aiTemplateDeletingId}
        aiTemplateLimitReached={aiTemplateLimitReached}
        aiBackupExporting={aiBackupExporting}
        aiBackupExportProgress={aiBackupExportProgress}
        aiBackupImporting={aiBackupImporting}
        aiBackupImportFileName={aiBackupImportFileName}
        aiBackupImportTargetProjectId={aiBackupImportTargetProjectId}
        setAiBackupImportTargetProjectId={setAiBackupImportTargetProjectId}
        aiBackupLastReport={aiBackupLastReport}
        handleExportAiTemplateBackup={handleExportAiTemplateBackup}
        handleSelectAiBackupImportFile={handleSelectAiBackupImportFile}
        handleImportAiTemplateBackup={handleImportAiTemplateBackup}
        queryTemplates={queryTemplates}
        newTemplateName={newTemplateName}
        setNewTemplateName={setNewTemplateName}
        newTemplateQuery={newTemplateQuery}
        setNewTemplateQuery={setNewTemplateQuery}
        handleCreateQueryTemplate={handleCreateQueryTemplate}
        queryTemplatesSaving={queryTemplatesSaving}
        queryTemplatesLoading={queryTemplatesLoading}
        editingQueryTemplateId={editingQueryTemplateId}
        queryTemplatesDeletingId={queryTemplatesDeletingId}
        editingQueryTemplateName={editingQueryTemplateName}
        setEditingQueryTemplateName={setEditingQueryTemplateName}
        editingQueryTemplateQuery={editingQueryTemplateQuery}
        setEditingQueryTemplateQuery={setEditingQueryTemplateQuery}
        handleCancelEditQueryTemplate={handleCancelEditQueryTemplate}
        handleSaveEditQueryTemplate={handleSaveEditQueryTemplate}
        handleStartEditQueryTemplate={handleStartEditQueryTemplate}
        handleDeleteQueryTemplate={handleDeleteQueryTemplate}
        handleAddOcrApiKey={handleAddOcrApiKey}
        handleChangeOcrApiKey={handleChangeOcrApiKey}
        handleRemoveOcrApiKey={handleRemoveOcrApiKey}
        loadQueryTemplates={loadQueryTemplates}
      />

      <EditFieldTemplateDialog
        open={aiTemplateEditDialogOpen}
        templateId={aiTemplateEditingId}
        name={aiTemplateEditingName}
        fieldJson={aiTemplateEditingFieldsJson}
        saving={aiTemplateEditSaving || aiTemplateEditLoading}
        onOpenChange={handleAiTemplateEditDialogOpenChange}
        onChangeName={setAiTemplateEditingName}
        onChangeFieldJson={setAiTemplateEditingFieldsJson}
        onConfirmSave={() => void handleConfirmEditAiTemplate()}
      />

      <Dialog open={aiTemplateDeleteDialogOpen} onOpenChange={handleCloseAiTemplateDeleteDialog}>
        <DialogContent className="border-0 bg-transparent p-0 shadow-none sm:max-w-[520px]">
          <DialogTitle className="sr-only">{t('projectHub.dialogs.deleteTemplate.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectHub.dialogs.deleteTemplate.srDescription')}
          </DialogDescription>
          <Card className="rounded-2xl border-slate-200 bg-white shadow-[0_32px_80px_-30px_rgba(15,23,42,0.5)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-semibold text-slate-900">{t('projectHub.dialogs.deleteTemplate.title')}</CardTitle>
              <CardDescription className="text-sm text-slate-500">
                {t('projectHub.dialogs.deleteTemplate.messagePrefix')}{' '}
                <span className="font-medium text-slate-700">
                  {aiTemplatePendingDelete?.name?.trim() || aiTemplatePendingDelete?.firstFieldLabel || aiTemplatePendingDelete?.id || t('projectHub.dialogs.deleteTemplate.thisTemplate')}
                </span>
                {t('projectHub.dialogs.deleteTemplate.messageSuffix')}
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                variant="outline"
                onClick={() => handleCloseAiTemplateDeleteDialog(false)}
                className="border-primary/35 bg-white text-primary hover:bg-primary/10"
                disabled={!!aiTemplateDeletingId}
              >
                {t('common.dialog.cancel')}
              </Button>
              <Button
                onClick={() => void handleConfirmDeleteAiTemplate()}
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={!!aiTemplateDeletingId}
              >
                {aiTemplateDeletingId ? t('projectHub.dialogs.deleteTemplate.deleting') : t('projectHub.actions.delete')}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>

      {/* Delete Project Dialog */}
      <Dialog open={state.showDeleteDialog} onOpenChange={handleCloseDeleteDialog}>
        <DialogContent className="border-0 bg-transparent p-0 shadow-none sm:max-w-[520px]">
          <DialogTitle className="sr-only">{t('projectHub.dialogs.deleteProject.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectHub.dialogs.deleteProject.srDescription')}
          </DialogDescription>
          <Card className="rounded-2xl border-slate-200 bg-white shadow-[0_32px_80px_-30px_rgba(15,23,42,0.5)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-semibold text-slate-900">{t('projectHub.dialogs.deleteProject.title')}</CardTitle>
              <CardDescription className="text-sm text-slate-500">
                {t('projectHub.dialogs.deleteProject.messagePrefix')}
                {' '}
                <span className="font-medium text-slate-700">{state.pendingDeleteProjectName || t('projectHub.dialogs.deleteProject.thisProject')}</span>
                {t('projectHub.dialogs.deleteProject.messageSuffix')}
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                variant="outline"
                onClick={handleCloseDeleteDialog}
                className="border-primary/35 bg-white text-primary hover:bg-primary/10"
              >
                {t('common.dialog.cancel')}
              </Button>
              <Button
                onClick={handleDeleteProject}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {t('projectHub.actions.delete')}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  );
}
